/**
 * Built-in Condition Evaluators
 *
 * Client-side evaluation functions for all built-in condition types.
 * These are registered with the condition registry.
 * All evaluators are async to support lazy-loading
 * NO React imports allowed in this file.
 */

import type {
	TargetingCondition,
	EvaluationContext,
	ConditionEvaluationResult,
} from './types';
import type {
	UrlConditionValue,
	QueryParamConditionValue,
	DeviceConditionValue,
	BrowserConditionValue,
	TimeConditionValue,
	DateRangeConditionValue,
	DayConditionValue,
	CookieConditionValue,
	TrafficPercentageValue,
	DayOfWeek,
} from '../types/targeting';

// Import lazy-loaded UA detection functions
import { detectDevice, detectBrowser, detectOS } from './ua-parser';

/**
 * Get current day of week
 */
export function getDayOfWeek(date: Date): DayOfWeek {
	const days: DayOfWeek[] = [
		'sunday',
		'monday',
		'tuesday',
		'wednesday',
		'thursday',
		'friday',
		'saturday',
	];
	return days[date.getDay()];
}

/**
 * Parse time string (HH:MM) to minutes since midnight
 */
export function parseTimeToMinutes(time: string): number {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
}

/**
 * Get current time in minutes since midnight
 */
export function getCurrentTimeInMinutes(date: Date): number {
	return date.getHours() * 60 + date.getMinutes();
}

const GEOLOCATION_CACHE_KEY = 'gp_popups_geoip_country';
const GEOLOCATION_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const GEOLOCATION_FETCH_TIMEOUT_MS = 5000; // 5 seconds

let cachedVisitorCountryCode: string | null | undefined;
let pendingVisitorCountryLookup: Promise<string | null> | null = null;

function normalizeCountryCode(countryCode: string): string | null {
	const normalized = countryCode.trim().toUpperCase();
	return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

export function parseGeolocationCountryCodes(value: unknown): string[] {
	if (!Array.isArray(value)) {
		return [];
	}

	const uniqueCodes = new Set<string>();
	for (const item of value) {
		if (typeof item === 'string') {
			const normalized = normalizeCountryCode(item);
			if (normalized) {
				uniqueCodes.add(normalized);
			}
		}
	}
	return Array.from(uniqueCodes);
}

function getLocalStorage(): Storage | null {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		return window.localStorage;
	} catch (e) {
		return null;
	}
}

function getCachedGeoLocationCountry(): string | null {
	const storage = getLocalStorage();
	if (!storage) {
		return null;
	}

	let rawCache: string | null = null;
	try {
		rawCache = storage.getItem(GEOLOCATION_CACHE_KEY);
	} catch {
		return null;
	}

	if (!rawCache) {
		return null;
	}

	try {
		const parsed = JSON.parse(rawCache) as {
			countryCode?: string;
			expires?: number;
		};

		if (
			typeof parsed.countryCode === 'string' &&
			typeof parsed.expires === 'number' &&
			parsed.expires > Date.now()
		) {
			return normalizeCountryCode(parsed.countryCode);
		}
	} catch (e) {
		// Cache is malformed, ignore and clear it below.
	}

	try {
		storage.removeItem(GEOLOCATION_CACHE_KEY);
	} catch {
		// Ignore storage errors
	}
	return null;
}

function setCachedGeoLocationCountry(countryCode: string): void {
	const storage = getLocalStorage();
	if (!storage) {
		return;
	}

	try {
		storage.setItem(
			GEOLOCATION_CACHE_KEY,
			JSON.stringify({
				countryCode,
				expires: Date.now() + GEOLOCATION_CACHE_TTL_MS,
			})
		);
	} catch {
		// Ignore quota/disabled storage errors
	}
}

async function getVisitorCountryCode(): Promise<string | null> {
	if (cachedVisitorCountryCode !== undefined) {
		return cachedVisitorCountryCode;
	}

	const cachedCountry = getCachedGeoLocationCountry();
	if (cachedCountry) {
		cachedVisitorCountryCode = cachedCountry;
		return cachedCountry;
	}

	if (pendingVisitorCountryLookup) {
		return pendingVisitorCountryLookup;
	}

	if (typeof fetch !== 'function') {
		cachedVisitorCountryCode = null;
		return null;
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(
		() => controller.abort(),
		GEOLOCATION_FETCH_TIMEOUT_MS
	);

	pendingVisitorCountryLookup = fetch('https://ipinfo.io/json', {
		method: 'GET',
		headers: {
			Accept: 'application/json',
		},
		signal: controller.signal,
	})
		.then(async (response) => {
			if (!response.ok) {
				return null;
			}

			const payload = (await response.json()) as { country?: unknown };
			if (typeof payload.country !== 'string') {
				return null;
			}

			const countryCode = normalizeCountryCode(payload.country);
			if (!countryCode) {
				return null;
			}

			setCachedGeoLocationCountry(countryCode);
			return countryCode;
		})
		.catch(() => null)
		.then((countryCode) => {
			clearTimeout(timeoutId);
			cachedVisitorCountryCode = countryCode;
			return countryCode;
		})
		.finally(() => {
			pendingVisitorCountryLookup = null;
		});

	return pendingVisitorCountryLookup;
}

export function resetGeoIpLookupCache(): void {
	cachedVisitorCountryCode = undefined;
	pendingVisitorCountryLookup = null;

	const storage = getLocalStorage();
	if (storage) {
		try {
			storage.removeItem(GEOLOCATION_CACHE_KEY);
		} catch {
			// Ignore storage errors to avoid crashing in restricted/blocked storage environments
		}
	}
}

// ============================================================================
// Individual Condition Evaluators (all async to support lazy-loading)
// ============================================================================

/**
 * Evaluate URL contains condition
 */
export async function evaluateUrlContains(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as UrlConditionValue;
	const url = context.url.href;
	const pattern = value.pattern;
	const caseSensitive = value.caseSensitive ?? false;

	const searchUrl = caseSensitive ? url : url.toLowerCase();
	const searchPattern = caseSensitive ? pattern : pattern.toLowerCase();

	const contains = searchUrl.includes(searchPattern);
	const matched = condition.operator === 'contains' ? contains : !contains;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `URL ${condition.operator === 'contains' ? 'contains' : 'does not contain'} "${pattern}"`
			: `URL ${condition.operator === 'contains' ? 'does not contain' : 'contains'} "${pattern}"`,
	};
}

/**
 * Evaluate URL matches (regex) condition
 */
export async function evaluateUrlMatches(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as UrlConditionValue;
	const url = context.url.href;

	try {
		const flags = value.caseSensitive ? '' : 'i';
		const regex = new RegExp(value.pattern, flags);
		const matched = regex.test(url);

		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched,
			reason: matched
				? `URL matches pattern "${value.pattern}"`
				: `URL does not match pattern "${value.pattern}"`,
		};
	} catch (e) {
		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched: false,
			reason: `Invalid regex pattern: ${value.pattern}`,
		};
	}
}

/**
 * Evaluate URL path condition
 */
export async function evaluateUrlPath(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as UrlConditionValue;
	const path = context.url.pathname;
	const pattern = value.pattern;
	const caseSensitive = value.caseSensitive ?? false;

	const comparePath = caseSensitive ? path : path.toLowerCase();
	const comparePattern = caseSensitive ? pattern : pattern.toLowerCase();

	let matched = false;
	let reason = '';

	switch (condition.operator) {
		case 'is':
			matched = comparePath === comparePattern;
			reason = matched
				? `Path is "${pattern}"`
				: `Path "${path}" is not "${pattern}"`;
			break;
		case 'is_not':
			matched = comparePath !== comparePattern;
			reason = matched
				? `Path is not "${pattern}"`
				: `Path "${path}" is "${pattern}"`;
			break;
		case 'starts_with':
			matched = comparePath.startsWith(comparePattern);
			reason = matched
				? `Path starts with "${pattern}"`
				: `Path "${path}" does not start with "${pattern}"`;
			break;
		case 'ends_with':
			matched = comparePath.endsWith(comparePattern);
			reason = matched
				? `Path ends with "${pattern}"`
				: `Path "${path}" does not end with "${pattern}"`;
			break;
		default:
			reason = `Unsupported operator: ${condition.operator}`;
	}

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason,
	};
}

/**
 * Evaluate query parameter condition
 */
export async function evaluateQueryParam(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as QueryParamConditionValue;
	const params = context.url.searchParams;
	const paramExists = params.has(value.paramName);
	const paramValue = params.get(value.paramName) ?? '';

	let matched = false;
	let reason = '';

	switch (condition.operator) {
		case 'exists':
			matched = paramExists;
			reason = matched
				? `Parameter "${value.paramName}" exists`
				: `Parameter "${value.paramName}" does not exist`;
			break;
		case 'not_exists':
			matched = !paramExists;
			reason = matched
				? `Parameter "${value.paramName}" does not exist`
				: `Parameter "${value.paramName}" exists`;
			break;
		case 'is':
			matched = paramExists && paramValue === value.paramValue;
			reason = matched
				? `Parameter "${value.paramName}" is "${value.paramValue}"`
				: `Parameter "${value.paramName}" is not "${value.paramValue}"`;
			break;
		case 'is_not':
			matched = !paramExists || paramValue !== value.paramValue;
			reason = matched
				? `Parameter "${value.paramName}" is not "${value.paramValue}"`
				: `Parameter "${value.paramName}" is "${value.paramValue}"`;
			break;
		case 'contains':
			matched =
				paramExists && paramValue.includes(value.paramValue || '');
			reason = matched
				? `Parameter "${value.paramName}" contains "${value.paramValue}"`
				: `Parameter "${value.paramName}" does not contain "${value.paramValue}"`;
			break;
		default:
			reason = `Unsupported operator: ${condition.operator}`;
	}

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason,
	};
}

/**
 * Evaluate referrer condition
 */
export async function evaluateReferrer(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as UrlConditionValue;
	const referrer = context.referrer;
	const pattern = value.pattern;
	const caseSensitive = value.caseSensitive ?? false;

	const compareReferrer = caseSensitive ? referrer : referrer.toLowerCase();
	const comparePattern = caseSensitive ? pattern : pattern.toLowerCase();

	let matched = false;
	let reason = '';

	switch (condition.operator) {
		case 'is':
			matched = compareReferrer === comparePattern;
			reason = matched
				? `Referrer is "${pattern}"`
				: `Referrer is not "${pattern}"`;
			break;
		case 'is_not':
			matched = compareReferrer !== comparePattern;
			reason = matched
				? `Referrer is not "${pattern}"`
				: `Referrer is "${pattern}"`;
			break;
		case 'contains':
			matched = compareReferrer.includes(comparePattern);
			reason = matched
				? `Referrer contains "${pattern}"`
				: `Referrer does not contain "${pattern}"`;
			break;
		case 'starts_with':
			matched = compareReferrer.startsWith(comparePattern);
			reason = matched
				? `Referrer starts with "${pattern}"`
				: `Referrer does not start with "${pattern}"`;
			break;
		default:
			reason = `Unsupported operator: ${condition.operator}`;
	}

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason,
	};
}

/**
 * Evaluate geolocation condition (country-by-IP via ipinfo.io).
 */
export async function evaluateGeolocation(
	condition: TargetingCondition,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const targetCountries = parseGeolocationCountryCodes(condition.value);

	if (targetCountries.length === 0) {
		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched: false,
			reason: 'No target countries configured',
		};
	}

	const visitorCountry = await getVisitorCountryCode();
	if (!visitorCountry) {
		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched: false,
			reason: 'Unable to determine visitor country',
		};
	}

	const isInList = targetCountries.includes(visitorCountry);
	const matched = condition.operator === 'is' ? isInList : !isInList;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Country "${visitorCountry}" ${condition.operator === 'is' ? 'is in' : 'is not in'} [${targetCountries.join(', ')}]`
			: `Country "${visitorCountry}" ${condition.operator === 'is' ? 'is not in' : 'is in'} [${targetCountries.join(', ')}]`,
	};
}

/**
 * Evaluate device condition (lazy-loads Bowser)
 */
export async function evaluateDevice(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as DeviceConditionValue;
	const currentDevice = await detectDevice(context.userAgent);
	const targetDevices = value.devices;

	const isInList = targetDevices.includes(currentDevice);
	const matched = condition.operator === 'is' ? isInList : !isInList;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Device "${currentDevice}" ${condition.operator === 'is' ? 'is in' : 'is not in'} [${targetDevices.join(', ')}]`
			: `Device "${currentDevice}" ${condition.operator === 'is' ? 'is not in' : 'is in'} [${targetDevices.join(', ')}]`,
	};
}

/**
 * Evaluate browser condition (lazy-loads Bowser)
 */
export async function evaluateBrowser(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as BrowserConditionValue;
	const currentBrowser = await detectBrowser(context.userAgent);
	const targetBrowsers = value.browsers;

	const isInList = targetBrowsers.includes(
		currentBrowser as BrowserConditionValue['browsers'][number]
	);
	const matched = condition.operator === 'is' ? isInList : !isInList;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Browser "${currentBrowser}" ${condition.operator === 'is' ? 'is in' : 'is not in'} [${targetBrowsers.join(', ')}]`
			: `Browser "${currentBrowser}" ${condition.operator === 'is' ? 'is not in' : 'is in'} [${targetBrowsers.join(', ')}]`,
	};
}

/**
 * Evaluate OS condition (lazy-loads Bowser)
 */
export async function evaluateOS(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const targetOS = condition.value as string;
	const currentOS = await detectOS(context.userAgent);

	const isMatch = currentOS === targetOS;
	const matched = condition.operator === 'is' ? isMatch : !isMatch;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `OS "${currentOS}" ${condition.operator === 'is' ? 'is' : 'is not'} "${targetOS}"`
			: `OS "${currentOS}" ${condition.operator === 'is' ? 'is not' : 'is'} "${targetOS}"`,
	};
}

/**
 * Evaluate time of day condition
 */
export async function evaluateTimeOfDay(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as TimeConditionValue;
	const currentMinutes = getCurrentTimeInMinutes(context.now);
	const startMinutes = parseTimeToMinutes(value.startTime);
	const endMinutes = parseTimeToMinutes(value.endTime);

	let matched: boolean;
	if (startMinutes <= endMinutes) {
		// Normal range (e.g., 09:00 to 17:00)
		matched =
			currentMinutes >= startMinutes && currentMinutes <= endMinutes;
	} else {
		// Overnight range (e.g., 22:00 to 06:00)
		matched =
			currentMinutes >= startMinutes || currentMinutes <= endMinutes;
	}

	const currentTime = `${Math.floor(currentMinutes / 60)
		.toString()
		.padStart(
			2,
			'0'
		)}:${(currentMinutes % 60).toString().padStart(2, '0')}`;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Current time ${currentTime} is between ${value.startTime} and ${value.endTime}`
			: `Current time ${currentTime} is not between ${value.startTime} and ${value.endTime}`,
	};
}

/**
 * Evaluate day of week condition
 */
export async function evaluateDayOfWeek(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as DayConditionValue;
	const currentDay = getDayOfWeek(context.now);
	const targetDays = value.days;

	const isInList = targetDays.includes(currentDay);
	const matched = condition.operator === 'is' ? isInList : !isInList;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Day "${currentDay}" ${condition.operator === 'is' ? 'is in' : 'is not in'} [${targetDays.join(', ')}]`
			: `Day "${currentDay}" ${condition.operator === 'is' ? 'is not in' : 'is in'} [${targetDays.join(', ')}]`,
	};
}

/**
 * Evaluate date range condition
 */
export async function evaluateDateRange(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as DateRangeConditionValue;
	const now = context.now;

	// Parse start and end dates
	const startDate = new Date(value.startDate);
	const endDate = new Date(value.endDate);

	// Set times if provided
	if (value.startTime) {
		const [hours, minutes] = value.startTime.split(':').map(Number);
		startDate.setHours(hours, minutes, 0, 0);
	} else {
		startDate.setHours(0, 0, 0, 0);
	}

	if (value.endTime) {
		const [hours, minutes] = value.endTime.split(':').map(Number);
		endDate.setHours(hours, minutes, 59, 999);
	} else {
		endDate.setHours(23, 59, 59, 999);
	}

	const matched = now >= startDate && now <= endDate;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Current date is within range ${value.startDate} to ${value.endDate}`
			: `Current date is outside range ${value.startDate} to ${value.endDate}`,
	};
}

/**
 * Evaluate cookie condition
 */
export async function evaluateCookie(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const value = condition.value as CookieConditionValue;
	const cookies = context.cookies;
	const cookieExists = value.cookieName in cookies;
	const cookieValue = cookies[value.cookieName] ?? '';

	let matched = false;
	let reason = '';

	switch (condition.operator) {
		case 'exists':
			matched = cookieExists;
			reason = matched
				? `Cookie "${value.cookieName}" exists`
				: `Cookie "${value.cookieName}" does not exist`;
			break;
		case 'not_exists':
			matched = !cookieExists;
			reason = matched
				? `Cookie "${value.cookieName}" does not exist`
				: `Cookie "${value.cookieName}" exists`;
			break;
		case 'is':
			matched = cookieExists && cookieValue === value.cookieValue;
			reason = matched
				? `Cookie "${value.cookieName}" is "${value.cookieValue}"`
				: `Cookie "${value.cookieName}" is not "${value.cookieValue}"`;
			break;
		case 'is_not':
			matched = !cookieExists || cookieValue !== value.cookieValue;
			reason = matched
				? `Cookie "${value.cookieName}" is not "${value.cookieValue}"`
				: `Cookie "${value.cookieName}" is "${value.cookieValue}"`;
			break;
		case 'contains':
			matched =
				cookieExists && cookieValue.includes(value.cookieValue || '');
			reason = matched
				? `Cookie "${value.cookieName}" contains "${value.cookieValue}"`
				: `Cookie "${value.cookieName}" does not contain "${value.cookieValue}"`;
			break;
		default:
			reason = `Unsupported operator: ${condition.operator}`;
	}

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason,
	};
}

/**
 * Evaluate custom JavaScript condition
 */
export async function evaluateCustom(
	condition: TargetingCondition,
	context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const code = condition.value as string;

	try {
		// Create a function from the code string
		// The code should return true or false
		// eslint-disable-next-line no-new-func
		const fn = new Function('window', 'document', 'location', code);
		const result = fn(
			context.windowContext || window,
			context.windowContext?.document || document,
			context.url
		);

		const matched = Boolean(result);

		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched,
			reason: matched
				? 'Custom condition returned true'
				: 'Custom condition returned false',
		};
	} catch (e) {
		return {
			conditionId: condition.id,
			conditionType: condition.type,
			matched: false,
			reason: `Custom condition error: ${e instanceof Error ? e.message : String(e)}`,
		};
	}
}

/**
 * Cookie name for storing the visitor number
 */
const VISITOR_NUMBER_COOKIE = 'gp_popups_visitor';

/**
 * Get or generate the shared random value for traffic percentage conditions.
 * This value is stored in a cookie so it persists across page loads,
 * enabling consistent A/B testing experiences for visitors.
 */
let sharedRandomValue: number | null = null;

/**
 * Parse cookies from document.cookie
 */
function getCookieValue(name: string): string | null {
	const nameEQ = `${name}=`;
	const cookies = document.cookie.split(';');
	for (let i = 0; i < cookies.length; i++) {
		let cookie = cookies[i];
		while (cookie.charAt(0) === ' ') {
			cookie = cookie.substring(1);
		}
		if (cookie.indexOf(nameEQ) === 0) {
			return decodeURIComponent(cookie.substring(nameEQ.length));
		}
	}
	return null;
}

/**
 * Set a cookie
 */
function setCookieValue(name: string, value: string, days: number): void {
	let expires = '';
	if (days > 0) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = `; expires=${date.toUTCString()}`;
	}
	document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
}

export function getSharedRandomValue(): number {
	if (sharedRandomValue === null) {
		// Try to get from cookie first
		const cookieValue = getCookieValue(VISITOR_NUMBER_COOKIE);
		if (cookieValue) {
			const parsed = parseInt(cookieValue, 10);
			if (!isNaN(parsed) && parsed >= 1 && parsed <= 100) {
				sharedRandomValue = parsed;
			}
		}

		// If no valid cookie, generate new value and store it
		if (sharedRandomValue === null) {
			sharedRandomValue = Math.floor(Math.random() * 100) + 1;
			// Store for 365 days for consistent A/B testing
			setCookieValue(
				VISITOR_NUMBER_COOKIE,
				String(sharedRandomValue),
				365
			);
		}
	}
	return sharedRandomValue;
}

/**
 * Reset the shared random value (useful for testing)
 * Also clears the cookie
 */
export function resetSharedRandomValue(): void {
	sharedRandomValue = null;
	// Delete the cookie by setting expiry in the past
	setCookieValue(VISITOR_NUMBER_COOKIE, '', -1);
}

/**
 * Set a specific visitor number (for admin bar testing)
 * @param value - The visitor number to set (1-100)
 */
export function setSharedRandomValue(value: number): void {
	if (value >= 1 && value <= 100) {
		sharedRandomValue = value;
		setCookieValue(VISITOR_NUMBER_COOKIE, String(value), 365);
	}
}

/**
 * Get the cookie name used for visitor number (for admin bar display)
 */
export function getVisitorNumberCookieName(): string {
	return VISITOR_NUMBER_COOKIE;
}

/**
 * Evaluate traffic percentage condition.
 * Shows the popup to a percentage of page views based on a range.
 * The random value is shared across all popups on the page.
 *
 * For A/B testing, use non-overlapping ranges:
 * - Variant A: { min: 1, max: 50 }
 * - Variant B: { min: 51, max: 100 }
 */
export async function evaluateTrafficPercentage(
	condition: TargetingCondition,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_context: EvaluationContext
): Promise<ConditionEvaluationResult> {
	const { min, max } = condition.value as TrafficPercentageValue;
	const randomValue = getSharedRandomValue();

	const matched = randomValue >= min && randomValue <= max;

	return {
		conditionId: condition.id,
		conditionType: condition.type,
		matched,
		reason: matched
			? `Visitor #${randomValue} is within range ${min}-${max}`
			: `Visitor #${randomValue} is outside range ${min}-${max}`,
	};
}
